import type { ComponentProps } from "react";

import { PRODUCT_NAME } from "@src/shared/brand";
import { navigationV2 } from "@src/shared/navigation/v2";

import type { TourProps, TourStepConfig, TourStepProps } from "../../ui";
import { Tour, TourStep } from "../../ui";
import {
  addVisitTourAgendaItem,
  cleanupVisitTourCache,
  cleanupVisitTourCacheAction,
  resetVisitTourRecordingAction,
  revealVisitTourAgendaItems,
  seedVisitTourPatientCache,
  startVisitTourRecording,
} from "./visit-tour.cache";
import { VISIT_TOUR_ENCOUNTER_ID } from "./visit-tour.fixtures";

export const VISIT_TOUR_ID = "visit";

export const VISIT_TOUR_STEP_IDS = {
  createPatient: "create-patient",
  agendaItems: "agenda-items",
  addAgendaItem: "add-agenda-item",
  fasterInputs: "faster-inputs",
  startVisit: "start-visit",
  note: "note",
  codes: "codes",
} as const;

export const VISIT_TOUR_TRIGGERS = {
  agendaItemAdded: "agenda-item-added",
  visitCompleted: "visit-completed",
} as const;

type VisitTourTargetProps = Omit<TourStepProps, "tour" | "step">;

export interface VisitTourProps extends Omit<TourProps, "id" | "steps"> {
  steps?: readonly TourStepConfig[];
}

const cleanupOnSkip = {
  onSkip: {
    callback: cleanupVisitTourCacheAction,
  },
};

export const visitTourSteps = [
  {
    id: VISIT_TOUR_STEP_IDS.createPatient,
    title: "Start with a patient",
    description:
      "Search for an existing patient or add a new one. Everything in the visit, including the note and codes, stays attached to this patient.",
    placement: "bottom-start",
    action: {
      ...cleanupOnSkip,
      onEnter: {
        hideUI: true,
        callback: ({ navigate, queryClient }) => {
          const previousPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

          navigate(navigationV2.newVisit());
          seedVisitTourPatientCache(queryClient);

          return () => {
            cleanupVisitTourCache(queryClient);
            navigate(previousPath);
          };
        },
      },
      onNext: {
        hideUI: true,
        callback: async ({ navigate, queryClient }) => {
          navigate(navigationV2.visit(VISIT_TOUR_ENCOUNTER_ID));
          await revealVisitTourAgendaItems(queryClient);

          return () => {
            navigate(navigationV2.newVisit());
            seedVisitTourPatientCache(queryClient);
          };
        },
      },
    },
  },
  {
    id: VISIT_TOUR_STEP_IDS.agendaItems,
    title: "Agenda items",
    description: (
      <>
        Agenda items are what you plan to cover during the visit. When you
        scribe, each one checks off automatically as you talk about it. You can
        add your own here, or teammates can add items to help you prep.
        <br />
        <br />
        <span className="text-foreground italic">
          For this test visit, we&apos;ve loaded a few examples.
        </span>
      </>
    ),
    placement: "right",
    action: cleanupOnSkip,
  },
  {
    id: VISIT_TOUR_STEP_IDS.addAgendaItem,
    title: "Add an agenda item",
    description:
      "Type one thing you want to cover, then add it to the agenda. During the visit, items check off automatically as you talk about them.",
    placement: "top-start",
    action: cleanupOnSkip,
    actions: {
      addAgendaItem: {
        event: VISIT_TOUR_TRIGGERS.agendaItemAdded,
        matches: (event) => {
          const description =
            typeof event.payload.description === "string"
              ? event.payload.description
              : "";
          return (
            event.payload.encounterId === VISIT_TOUR_ENCOUNTER_ID &&
            description.trim().length > 0
          );
        },
        action: ({ event, queryClient }) => {
          const description =
            typeof event.payload.description === "string"
              ? event.payload.description
              : "";
          if (description.trim()) {
            addVisitTourAgendaItem(queryClient, description);
          }
        },
      },
    },
    completion: {
      event: VISIT_TOUR_TRIGGERS.agendaItemAdded,
      autoAdvance: true,
      instructions: "Type an agenda item and press Enter or Add to continue.",
      matches: (event) => {
        const description =
          typeof event.payload.description === "string"
            ? event.payload.description
            : "";
        return (
          event.payload.encounterId === VISIT_TOUR_ENCOUNTER_ID &&
          description.trim().length > 0
        );
      },
    },
  },
  {
    id: VISIT_TOUR_STEP_IDS.fasterInputs,
    title: "Two faster ways",
    description:
      "Drop a payer gap list to turn every care gap into an agenda item, or use voice to rattle off several items at once.",
    placement: "top-start",
    action: {
      ...cleanupOnSkip,
      onNext: {
        callback: ({ navigate }) =>
          navigate(navigationV2.visit(VISIT_TOUR_ENCOUNTER_ID)),
      },
    },
  },
  {
    id: VISIT_TOUR_STEP_IDS.startVisit,
    title: "Start when you're ready",
    description: `Start recording and ${PRODUCT_NAME.singular} listens in, checking off agenda items as you cover them.`,
    placement: "bottom-end",
    action: {
      ...cleanupOnSkip,
      onPrevious: {
        callback: resetVisitTourRecordingAction,
      },
    },
    actions: {
      startVisit: {
        event: VISIT_TOUR_TRIGGERS.visitCompleted,
        hideUI: true,
        action: ({ queryClient }) => startVisitTourRecording(queryClient),
      },
    },
    completion: {
      event: VISIT_TOUR_TRIGGERS.visitCompleted,
      autoAdvance: true,
      instructions:
        "Click Start visit to see agenda items check off during the simulated recording.",
    },
  },
  {
    id: VISIT_TOUR_STEP_IDS.note,
    title: "Your note is ready",
    description:
      "The note is generated from the encounter and scratchpad. Review it, then copy it into your EHR.",
    placement: "bottom-start",
    action: cleanupOnSkip,
  },
  {
    id: VISIT_TOUR_STEP_IDS.codes,
    title: "Codes from this visit",
    description:
      "Codes come from the visit evidence. Review the suggestions and copy the codes into your chart when they are ready.",
    placement: "top",
    action: {
      ...cleanupOnSkip,
      onComplete: {
        callback: ({ navigate, queryClient }) => {
          navigate(navigationV2.visits());
          window.setTimeout(() => cleanupVisitTourCache(queryClient), 0);
        },
      },
    },
  },
] as const satisfies readonly TourStepConfig[];

function VisitTourRoot({ steps = visitTourSteps, ...props }: VisitTourProps) {
  return <Tour {...props} id={VISIT_TOUR_ID} steps={steps} />;
}

function CreatePatientStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep
      {...props}
      step={VISIT_TOUR_STEP_IDS.createPatient}
      tour={VISIT_TOUR_ID}
    >
      {children}
    </TourStep>
  );
}

function AgendaItemsStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep
      {...props}
      step={VISIT_TOUR_STEP_IDS.agendaItems}
      tour={VISIT_TOUR_ID}
    >
      {children}
    </TourStep>
  );
}

function AddAgendaItemStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep
      {...props}
      step={VISIT_TOUR_STEP_IDS.addAgendaItem}
      tour={VISIT_TOUR_ID}
    >
      {children}
    </TourStep>
  );
}

function FasterInputsStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep
      {...props}
      step={VISIT_TOUR_STEP_IDS.fasterInputs}
      tour={VISIT_TOUR_ID}
    >
      {children}
    </TourStep>
  );
}

function StartVisitStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep
      {...props}
      step={VISIT_TOUR_STEP_IDS.startVisit}
      tour={VISIT_TOUR_ID}
    >
      {children}
    </TourStep>
  );
}

function NoteStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep {...props} step={VISIT_TOUR_STEP_IDS.note} tour={VISIT_TOUR_ID}>
      {children}
    </TourStep>
  );
}

function CodesStep({ children, ...props }: VisitTourTargetProps) {
  return (
    <TourStep {...props} step={VISIT_TOUR_STEP_IDS.codes} tour={VISIT_TOUR_ID}>
      {children}
    </TourStep>
  );
}

export const VisitTour = Object.assign(VisitTourRoot, {
  CreatePatientStep,
  AgendaItemsStep,
  AddAgendaItemStep,
  FasterInputsStep,
  StartVisitStep,
  NoteStep,
  CodesStep,
});

export type VisitTourComponentProps = ComponentProps<typeof VisitTour>;
